import React, {FC, useCallback, useEffect} from 'react';
import styled from 'styled-components';

import {directHoverSelector} from '../../../../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {KeyboardActionsEnum} from '../../../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ConversationSummaryStatesEnum} from '../../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {isConversationOnelinerFact} from '../../../../../../../libs/shared/core/src/models/api/factModel';
import {DataEventActionsEnum} from '../../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {ViewerDataEventObject} from '../../../../../../../libs/shared/core/src/models/tracking/objectNames/viewerDataEventObject';
import {useDataEventTracking} from '../../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {ViewerPanelId} from '../../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {ThreeDots} from '../../../../../../../libs/shared/ui/src/components/composer/threeDots';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {KeyboardEventHandlers} from '../../../../../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcuts} from '../../../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch} from '../../../../../../helpers/appReduxHelpers';
import {ConversationModel} from '../../../../../../models/api/conversationModel';
import {toggleSynopsisAction} from '../../../../../../store/panels/viewer/viewerPanelActions';
import {
  ViewerPanelPageState,
  ViewerPanelPageTypesEnum,
} from '../../../../../../store/panels/viewer/viewerPanelState';

/*
 * Props.
 */

interface ConversationSynopsisEntrypointProps {
  conversation: ConversationModel;
  page: ViewerPanelPageState;
  panelId: ViewerPanelId;
}

/*
 * Styles.
 */

const StyledChromelessButton = styled(ChromelessButton)`
  display: flex;
  flex-flow: row;
  gap: 8px;
  padding: 8px;
  align-items: center;
  width: 100%;
  height: 32px;
  overflow: hidden;
  border-radius: 4px;
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.small};
  line-height: 16px;
  white-space: nowrap;

  ${directHoverSelector} {
    background-color: ${(p) => p.theme.greys.shade30};
  }
`;

const StyledLeftIcon = styled(Icon)`
  grid-area: left;
  height: 16px;
  margin-right: 4px;
`;

const StyledOneLinerDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  ${ellipsis()};
`;

/*
 * Component.
 */

export const ConversationSynopsisEntrypoint: FC<ConversationSynopsisEntrypointProps> = ({
  conversation,
  page,
  panelId,
}) => {
  const theme = useUserTheme();

  const dispatch = useAppDispatch();
  const {trackDataEvent} = useDataEventTracking();

  const onelinerFact = conversation.facts.find(isConversationOnelinerFact);
  const isSynopsisOpen =
    page.type === ViewerPanelPageTypesEnum.SINGLE_CONVERSATION ? page.isSynopsisOpen : false;

  const toggleSynopsis = useCallback(() => {
    if (onelinerFact || isSynopsisOpen) {
      dispatch(toggleSynopsisAction(panelId, !isSynopsisOpen));
    }
  }, [dispatch, isSynopsisOpen, onelinerFact, panelId]);

  useEffect(() => {
    if (onelinerFact) {
      trackDataEvent(DataEventActionsEnum.VIEW, ViewerDataEventObject.SYNOPSIS, {
        conversation_id: conversation.id,
      });
    }
    // Trigger when user loads a new conversation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation.id]);

  const keyHandlers: KeyboardEventHandlers = {
    [KeyboardActionsEnum.TOGGLE_EXPANDED_CONVERSATION_SUMMARY]: toggleSynopsis,
  };

  if (!onelinerFact) {
    switch (conversation.summaryState) {
      case ConversationSummaryStatesEnum.LOADING: {
        return (
          <StyledChromelessButton isDisabled>
            <StyledLeftIcon name="sparklesAiFilledSmall" />
            <StyledOneLinerDiv>
              <Trans id="conversation-synopsis-entrypoint-loading">Summarizing</Trans>
              <ThreeDots size={VisualSizesEnum.SMALL} color={theme.palette.blue.shade40} />
            </StyledOneLinerDiv>
          </StyledChromelessButton>
        );
      }

      case ConversationSummaryStatesEnum.FAILED_RATE_LIMITED:
      case ConversationSummaryStatesEnum.FAILED_OTHER: {
        const label =
          conversation.summaryState === ConversationSummaryStatesEnum.FAILED_RATE_LIMITED ? (
            <Trans id="conversation-synopsis-error-rate-limit">
              Cannot generate summary due to daily rate limit. Try again later.
            </Trans>
          ) : (
            <Trans id="conversation-synopsis-error-generic">
              Could not generate summary. Please try again later or contact Front Support.
            </Trans>
          );

        return (
          <StyledChromelessButton isDisabled>
            <StyledLeftIcon name="warningFilledSmall" />
            <StyledOneLinerDiv>{label}</StyledOneLinerDiv>
          </StyledChromelessButton>
        );
      }

      default: {
        return null;
      }
    }
  }

  return (
    <KeyboardShortcuts handlers={keyHandlers}>
      <StyledChromelessButton onClick={() => toggleSynopsis()} hasHover={isSynopsisOpen}>
        <StyledLeftIcon name="sparklesAiFilledSmall" />
        <StyledOneLinerDiv>{appendEllipsis(onelinerFact.content)}</StyledOneLinerDiv>
      </StyledChromelessButton>
    </KeyboardShortcuts>
  );
};

// Always render an ellipsis to help discover that the summary is interactive.
function appendEllipsis(oneliner: string) {
  return `${oneliner.replace(/\.$/, '')}…`;
}
